/**
 * Row-edit draft helpers for Component Matching Import's row-edit modal.
 * Conceptually mirrors Pay Component Import's `payComponentRowEditorDraft.ts`,
 * but Matching's data model is a flat in-memory `MatchingRow[]` array (not a
 * CSV-text + column-mapping preview object) — so there is no CSV
 * reserialization step here. The Composer applies the returned `MatchingRow`
 * directly via `manager.updateRowsLocally` / `wizard.updateParsedRows`.
 */
import { assignmentDatePairErrors } from "./assignmentDates";
import { deriveRowDecisions } from "./deriveRowDecisions";
import type { MatchingCsvAction, MatchingRow } from "../types/matching";

/** Editable MatchAction — empty when the CSV verb was blank/invalid. */
export type MatchingRowEditMatchAction = MatchingCsvAction | "";

/** Editable field set as plain strings, suitable for text/select inputs. */
export interface MatchingRowEditDraft {
  payRunId: string;
  matchAction: MatchingRowEditMatchAction;
  employeeCode: string;
  componentCode: string;
  componentName: string;
  startDate: string;
  finishDate: string;
  targetAmount: string;
}

function parseDraftMatchAction(raw: string): MatchingCsvAction | "" {
  const v = raw
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (v === "APPLY") return "APPLY";
  if (v === "DELETE") return "DELETE";
  if (v === "REAPPLY") return "REAPPLY";
  if (v === "DELETE_KEEP_AMOUNT") return "DELETE_KEEP_AMOUNT";
  return "";
}

export function createMatchingRowEditDraft(row: MatchingRow): MatchingRowEditDraft {
  // Prefer rawMatchAction so invalid CSV verbs (e.g. CLEAR) stay unselected
  // instead of preselecting the APPLY stub used by parseMatchingCsv.
  return {
    payRunId: row.payRunId,
    matchAction: parseDraftMatchAction(row.rawMatchAction),
    employeeCode: row.employeeCode,
    componentCode: row.componentCode ?? "",
    componentName: row.componentName,
    startDate: row.startDate ?? "",
    finishDate: row.finishDate ?? "",
    targetAmount: row.targetAmount === null ? "" : String(row.targetAmount),
  };
}

export function isMatchingRowEditDraftDirty(
  original: MatchingRowEditDraft,
  draft: MatchingRowEditDraft
): boolean {
  return (Object.keys(original) as (keyof MatchingRowEditDraft)[]).some(
    (key) => original[key] !== draft[key]
  );
}

/** Parses a TargetAmount cell the same way `parseMatchingCsv` does. */
function parseDraftAmount(raw: string): number | null | "invalid" {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const cleaned = trimmed.replace(/[$,\s]/g, "");
  if (cleaned === "" || cleaned === "-") return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return "invalid";
  return n;
}

/**
 * Fast, client-side guardrails mirroring `parseMatchingCsv`'s per-row
 * validation rules (EmployeeCode/ComponentName required, StartDate/
 * FinishDate both-or-neither, TargetAmount required for APPLY, blank for
 * REAPPLY and DELETE_KEEP_AMOUNT, and a valid number). The ultimate source of truth is still the backend/local
 * `validate()` call re-run after the edit is saved — these just stop a user
 * from saving a row that would obviously fail that re-validation again.
 */
export function validateMatchingRowEditDraft(
  draft: MatchingRowEditDraft
): string[] {
  const errors: string[] = [];

  if (draft.matchAction === "") {
    errors.push("MatchAction is required.");
  }
  if (draft.employeeCode.trim() === "") {
    errors.push("EmployeeCode is required.");
  }
  if (draft.componentName.trim() === "") {
    errors.push("ComponentName is required.");
  }

  errors.push(
    ...assignmentDatePairErrors(draft.startDate, draft.finishDate)
  )

  const amount = parseDraftAmount(draft.targetAmount);
  if (amount === "invalid") {
    errors.push(`TargetAmount "${draft.targetAmount.trim()}" is not a valid number.`);
  } else if (draft.matchAction === "APPLY" && amount === null) {
    errors.push("TargetAmount is required for APPLY rows.");
  } else if (
    (draft.matchAction === "REAPPLY" ||
      draft.matchAction === "DELETE_KEEP_AMOUNT") &&
    amount !== null
  ) {
    errors.push(
      `TargetAmount must be blank for ${draft.matchAction}. Use APPLY to change the amount.`
    );
  }

  return errors;
}

/**
 * Applies a draft back onto the row, re-deriving `manualPay`/`amountMode`/
 * `action` via the same `deriveRowDecisions` helper `parseMatchingCsv` uses
 * at parse time, so those engine-path hints stay correct after an edit.
 * Callers should run `validateMatchingRowEditDraft` first — this function
 * assumes the draft is valid and will not throw on a bad amount (it falls
 * back to `null`).
 */
export function applyMatchingRowEditDraft(
  row: MatchingRow,
  draft: MatchingRowEditDraft
): MatchingRow {
  if (draft.matchAction === "") {
    throw new Error("MatchAction is required before applying a row edit.");
  }
  const matchAction = draft.matchAction;
  const parsedAmount = parseDraftAmount(draft.targetAmount);
  const targetAmount = parsedAmount === "invalid" ? null : parsedAmount;
  const decisions = deriveRowDecisions(matchAction, targetAmount);
  const componentCode = draft.componentCode.trim();
  const startDate = draft.startDate.trim();
  const finishDate = draft.finishDate.trim();

  return {
    ...row,
    // Pay run stays bound from the UI step — never editable in the row modal.
    payRunId: row.payRunId,
    matchAction,
    rawMatchAction: matchAction,
    employeeCode: draft.employeeCode.trim(),
    componentCode: componentCode === "" ? null : componentCode,
    componentName: draft.componentName.trim(),
    startDate: startDate === "" ? null : startDate,
    finishDate: finishDate === "" ? null : finishDate,
    targetAmount,
    rawAmount: draft.targetAmount,
    manualPay: decisions.manualPay,
    amountMode: decisions.amountMode,
    action: decisions.action,
  };
}
